import React, { useCallback, useMemo } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { castingColumns } from "./tableParams/castingColumns";
import MediaAPI from "../../api/MediaAPI";
import { selectPaginatedPage } from "../../hooks/selectPaginatedPage";
import useDataTableQuery from "../../hooks/useDataTableQuery";
import useDataTableServerState from "../../hooks/useDataTableServerState";
import CappedPage from "../common/CappedPage";
import CreateButton from "../common/CreateButton";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { castingPath, CASTINGS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type {
  CastingListItem,
  GetCastingListResponse,
} from "../../api/MediaAPI.types";

// Le préfixe couvre aussi la requête infinie des smartlists (`{ pages, pageParams }`) : on ne touche qu'aux pages.
function isCastingListPage(value: unknown): value is GetCastingListResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "data" in value &&
    Array.isArray(value.data)
  );
}

function CastingList(): JSX.Element {
  const { t } = useTranslation("editor-casting");
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mutate: deleteCasting } = useMutation({
    mutationFn: async (uuid: string) => MediaAPI.deleteCasting(uuid),
    onMutate: async (uuid) => {
      await queryClient.cancelQueries([MediaAPI.getCastingList.name]);

      const previousEntryList = queryClient
        .getQueriesData([MediaAPI.getCastingList.name])
        .filter(([, data]) => isCastingListPage(data));

      queryClient.setQueriesData(
        [MediaAPI.getCastingList.name],
        (old: unknown): unknown => {
          if (!isCastingListPage(old)) return old;

          const castingList = old.data.filter(
            (casting) => casting.uuid !== uuid,
          );
          const removedCount = old.data.length - castingList.length;
          return {
            ...old,
            data: castingList,
            page: {
              ...old.page,
              total_items: old.page.total_items - removedCount,
            },
          };
        },
      );

      return { previousEntryList };
    },
    onError: (_error, _uuid, context) => {
      context?.previousEntryList.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: async () =>
      queryClient.invalidateQueries([MediaAPI.getCastingList.name]),
  });

  const handleEdit = useCallback(
    (uuid: string): void => history.push(castingPath(uuid)),
    [history],
  );

  const columnList = useMemo(
    () => castingColumns({ t, onEdit: handleEdit, onDelete: deleteCasting }),
    [t, handleEdit, deleteCasting],
  );

  const serverState = useDataTableServerState<CastingListItem>({
    columns: columnList,
    defaultSorting: { id: "updated_at", desc: true },
  });

  const castingListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCastingList,
    params: [
      {
        page: serverState.page,
        perPage: serverState.perPage,
        search: serverState.search || null,
        order: serverState.order,
      },
    ],
  });

  const castingTable = useDataTableQuery(castingListQuery, selectPaginatedPage);

  const createButton = (
    <CreateButton href={`${CASTINGS_ROUTE}/new`} label={t("createCasting")} />
  );

  const emptyState = (
    <DataTableEmptyState
      action={createButton}
      icon={UserRound}
      message={t("createCastingText")}
      title={t("sectionTitle")}
    />
  );

  return (
    <CappedPage contentClassName="py-15">
      <DataTable
        columns={columnList}
        data={castingTable.data}
        emptyState={emptyState}
        getRowHref={(casting) => castingPath(casting.uuid)}
        headerActions={createButton}
        isError={castingTable.isError}
        isLoading={castingTable.isLoading}
        onRetry={castingTable.onRetry}
        rowCount={castingTable.rowCount}
        serverState={serverState}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default CastingList;
